import { useEffect, useState } from 'react';
import { Typography } from '../../../components/typography/typography';
import '../dispatch-v2.scss';
import Icon from '../../../components/icons/icon';
import { DatePicker } from '../../../components/date-picker/date-picker';
import { Button } from '../../../components/form/button';
import { SelectForm } from '../../../components/select-form/select-form';
import { Location } from '../../../clients/quipli-service/location/location';
import { Admin } from '../../../clients/quipli-service/admin/admin';
import { Modal } from '../../../components/modal/modal';
import {
  dateTimeFromRequest,
  DispatchSearchRequest,
  driversFromRequest,
  locationsFromRequest,
  typeOptions,
} from '../DispatchV2';
import classNames from 'classnames';
import { Checkbox } from '../../../components/checkbox/checkbox';

export const FilterToolbar = ({
  allLocations,
  allDrivers,
  setRequest,
  request,
  isLoading,
}: {
  allLocations: Location[];
  allDrivers: Admin[];
  request: DispatchSearchRequest;
  setRequest: React.Dispatch<React.SetStateAction<DispatchSearchRequest>>;
  isLoading: boolean;
}) => {
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [localRequest, setLocalRequest] = useState<DispatchSearchRequest>(request);

  const locationOptions =
    allLocations.length === 1 ? allLocations : [{ id: -1, name: 'All Locations' }, ...allLocations];

  const showFilterButton = true;
  const showFiltersInModal = allLocations.length + allDrivers.length > 5;

  // When in modal view, going to wait to set the request until the user closes the modal
  useEffect(() => {
    setLocalRequest(request); // Sync local state with server state after updates
  }, [request]);

  const handleRequestChange = (newRequest: React.SetStateAction<DispatchSearchRequest>) => {
    if (showFiltersInModal) {
      setLocalRequest(newRequest);
    } else {
      setRequest(newRequest);
    }
  };

  useEffect(() => {
    if (!mobileFiltersOpen) {
      setRequest(localRequest);
    }
  }, [mobileFiltersOpen]);

  const SelectDropdowns = () => (
    <>
      <SelectForm
        placeholder="Location"
        className="location w-max-content"
        styles={{ control: (base) => ({ ...base, width: 'max-content' }) }}
        value={locationsFromRequest(localRequest, allLocations).map((l) => ({
          label: l.name,
          value: l.id.toString(),
        }))}
        options={locationOptions.map((l) => ({ label: l.name, value: l.id.toString() }))}
        onChange={(newValue) => {
          if (newValue?.find((nv) => nv.value === '-1') != null) {
            handleRequestChange((prev) => ({
              ...prev,
              locations: allLocations.map((l) => l.id.toString()),
            }));
          } else {
            handleRequestChange((prev) => ({
              ...prev,
              locations: newValue ? newValue.map((nv) => nv.value.toString()) : [],
            }));
          }
        }}
        isClearable={allLocations.length > 1}
        isMulti={true}
        isDisabled={isLoading}
      />
      <SelectForm
        placeholder="Driver"
        className="driver"
        value={driversFromRequest(localRequest, allDrivers).map((d) => ({
          label: d.adminFullName,
          value: d.adminId.toString(),
        }))}
        options={[
          { label: 'All Drivers', value: '-1' },
          ...allDrivers.map((d) => ({ label: d.adminFullName, value: d.adminId.toString() })),
        ]}
        onChange={(newValue) => {
          if (newValue?.find((nv) => nv.value === '-1') != null) {
            handleRequestChange((prev) => ({
              ...prev,
              drivers: allDrivers.map((d) => d.adminId.toString()),
            }));
          } else {
            handleRequestChange((prev) => ({
              ...prev,
              drivers: newValue ? newValue.map((nv) => nv.value.toString()) : [],
            }));
          }
        }}
        isClearable={true}
        isMulti={true}
        isDisabled={isLoading}
      />
      <SelectForm
        placeholder="Type"
        defaultValue={typeOptions}
        options={typeOptions}
        value={{ value: localRequest.type, label: localRequest.type }}
        onChange={(newValue) => {
          handleRequestChange((prev) => ({ ...prev, type: newValue?.value ?? undefined }));
        }}
        isClearable={false}
        isDisabled={isLoading}
      />
    </>
  );

  return (
    <div className="filter-toolbar">
      <Modal
        title="Dispatch Filters"
        className="h-[80vh]"
        style={{ maxWidth: '100vw' }}
        isOpen={mobileFiltersOpen}
        close={() => setMobileFiltersOpen(false)}
        actions={
          <Button className="button button-secondary" onClick={() => setMobileFiltersOpen(false)}>
            Close
          </Button>
        }
      >
        <div
          className="flex flex-row gap-2 mt-2 cursor-pointer"
          onClick={() => handleRequestChange((prev) => ({ ...prev, hideQuotes: !localRequest.hideQuotes }))}
        >
          <Typography type="text-sm" weight="regular">
            Hide quotes
          </Typography>
          <Checkbox isChecked={localRequest.hideQuotes} />
        </div>
        {showFiltersInModal && <SelectDropdowns />}
      </Modal>
      <div className="left-portions">
        <div className="date-stuff">
          <DatePicker
            value={dateTimeFromRequest(request)}
            onChange={(dt) => setRequest((prev) => ({ ...prev, date: dt?.toFormat('MM-dd-yyyy') }))}
            arrowable={true}
            inputClassName="max-w-[25vw]"
          />
          <Button
            className="button button-secondary"
            onClick={() => setRequest((prev) => ({ ...prev, date: 'today' }))}
          >
            <div className="pl-2 pr-2">
              <Typography type="text-sm" className="p-2" weight="semi-bold">
                Today
              </Typography>
            </div>
          </Button>
        </div>
        <div className={classNames('mobile-filter', { showFilterButtonOverride: showFilterButton })}>
          <Button className="button button-secondary" onClick={() => setMobileFiltersOpen(true)}>
            <Icon icon="FilterLines" stroke="var(--grey-700)" />
          </Button>
        </div>
      </div>
      <div className={classNames('right-portions', { showFilterButtonOverride: showFiltersInModal })}>
        <SelectDropdowns />
      </div>
    </div>
  );
};
